import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiRefreshCw, FiChevronDown, FiChevronUp, FiTrash2, FiUpload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { listClaims, getClaim, deleteClaim, uploadReceipt, errMsg } from '../../utils/expenseApi';

const STATUS_POLLING = new Set(['SUBMITTED']);

const CATEGORY_LABELS = {
  TRAVEL_AIRFARE: 'Airfare', TRAVEL_HOTEL: 'Hotel',
  TRAVEL_GROUND: 'Ground Transport', TRAVEL_TRAIN: 'Train',
  MEALS: 'Meals', MEALS_CLIENT: 'Client Meals',
  OFFICE_SUPPLIES: 'Office Supplies', COMMUNICATION: 'Communication',
  CONFERENCE: 'Conference', FUEL: 'Fuel',
  VISA_PASSPORT: 'Visa / Passport', MEDICAL: 'Medical',
  MISCELLANEOUS: 'Miscellaneous',
};

const CAN_CANCEL = new Set(['DRAFT', 'REJECTED']);
const ALL_STATUSES = ['', 'DRAFT', 'SUBMITTED', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'POSTED', 'REIMBURSED', 'CANCELLED', 'ERROR'];

function StatusBadge({ status }) {
  return <span className={`exp-badge ${status}`}>{status.replace(/_/g, ' ')}</span>;
}

function PolicyBadge({ status }) {
  if (!status) return null;
  return <span className={`exp-badge ${status}`} style={{ fontSize: '0.68rem' }}>{status.replace(/_/g, ' ')}</span>;
}

function ClaimDetailPanel({ claimId, onClose }) {
  const [detail,   setDetail]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    getClaim(claimId).then(setDetail).catch(() => {}).finally(() => setLoading(false));
  }, [claimId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadReceipt(file, claimId);
      toast.success('Receipt uploaded. OCR running in background.');
    } catch (err) {
      toast.error(errMsg(err, 'Upload failed.'));
    }
    setUploading(false);
    e.target.value = '';
  };

  if (loading) return <div className="exp-spinner-wrap" style={{ padding: 20 }}><div className="exp-spinner" /></div>;
  if (!detail) return null;

  return (
    <motion.div
      className="exp-detail-panel"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.22 }}
      style={{ overflow: 'hidden' }}
    >
      <div className="exp-detail-grid">
        <div className="exp-detail-item"><span className="exp-detail-lbl">Claim ID</span><span className="exp-detail-val" style={{ fontSize: '0.76rem', fontFamily: 'monospace' }}>{detail.claim_id}</span></div>
        <div className="exp-detail-item"><span className="exp-detail-lbl">Company</span><span className="exp-detail-val">{detail.company_code}</span></div>
        <div className="exp-detail-item"><span className="exp-detail-lbl">Cost Centre</span><span className="exp-detail-val">{detail.cost_centre}</span></div>
        <div className="exp-detail-item"><span className="exp-detail-lbl">Currency</span><span className="exp-detail-val">{detail.currency_code}</span></div>
        <div className="exp-detail-item"><span className="exp-detail-lbl">Total Amount</span><span className="exp-detail-val" style={{ color: 'var(--ep)', fontWeight: 800 }}>{parseFloat(detail.total_amount).toLocaleString('en-IN')}</span></div>
        <div className="exp-detail-item"><span className="exp-detail-lbl">Policy Status</span><span className="exp-detail-val"><PolicyBadge status={detail.policy_status} /></span></div>
        {detail.sap_doc_number && <div className="exp-detail-item"><span className="exp-detail-lbl">SAP Doc</span><span className="exp-detail-val">{detail.sap_doc_number}</span></div>}
        {detail.error_message && <div className="exp-detail-item" style={{ gridColumn: '1 / -1' }}><span className="exp-detail-lbl">Error</span><span className="exp-detail-val" style={{ color: 'var(--e-err)' }}>{detail.error_message}</span></div>}
      </div>

      {/* Expense lines */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--e-muted)', marginBottom: 8 }}>
          Expense Lines ({detail.lines?.length || 0})
        </div>
        <div className="exp-table-wrap">
          <table className="exp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th>Date</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Justification</th>
              </tr>
            </thead>
            <tbody>
              {(detail.lines || []).map(l => (
                <tr key={l.line_id}>
                  <td style={{ color: 'var(--e-muted)' }}>{l.line_number}</td>
                  <td>{CATEGORY_LABELS[l.expense_category] || l.expense_category}</td>
                  <td>{l.expense_date}</td>
                  <td>{l.vendor_name || '—'}</td>
                  <td style={{ fontWeight: 700, color: 'var(--ep)' }}>
                    {parseFloat(l.amount).toLocaleString('en-IN')}
                    {l.tax_amount && parseFloat(l.tax_amount) > 0
                      ? <span style={{ fontSize: '0.72rem', color: 'var(--e-muted)', marginLeft: 4 }}>+{parseFloat(l.tax_amount).toLocaleString('en-IN')} tax</span>
                      : null}
                  </td>
                  <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.justification || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Receipt upload */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff" style={{ display: 'none' }} onChange={handleUpload} />
        <motion.button
          className="exp-btn small"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          whileTap={{ scale: 0.96 }}
        >
          {uploading ? <><div className="exp-inline-spinner" style={{ borderTopColor: 'var(--ep)', borderColor: 'var(--e-bdr)' }} /> Uploading…</> : <><FiUpload size={12} /> Upload Receipt</>}
        </motion.button>
        <span style={{ fontSize: '0.76rem', color: 'var(--e-muted)' }}>PDF, JPG, PNG up to 10 MB</span>
      </div>
    </motion.div>
  );
}

export default function MyClaims({ onNavigate }) {
  const [claims,      setClaims]      = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [expanded,    setExpanded]    = useState(null);
  const [cancelling,  setCancelling]  = useState(null);

  const PAGE_SIZE = 15;
  const pollingRef = useRef(null);

  const load = useCallback(async (p = page, status = filterStatus) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (status) params.status = status;
      const data = await listClaims(params);
      setClaims(data.items || []);
      setTotal(data.total || 0);
    } catch (_) {}
    setLoading(false);
  }, [page, filterStatus]);

  useEffect(() => { load(); }, [load]);

  // Poll in-flight claims every 4s
  useEffect(() => {
    const inFlight = claims.filter(c => STATUS_POLLING.has(c.status));
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (inFlight.length === 0) return;
    pollingRef.current = setInterval(() => {
      // Reload the page to get updated statuses
      load();
    }, 4000);
    return () => clearInterval(pollingRef.current);
  }, [claims, load]);

  const handleFilterChange = (s) => {
    setFilterStatus(s);
    setPage(1);
    load(1, s);
  };

  const handleCancel = async (claimId) => {
    if (!window.confirm('Cancel this claim? This cannot be undone.')) return;
    setCancelling(claimId);
    try {
      await deleteClaim(claimId);
      toast.success('Claim cancelled.');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not cancel claim.'));
    }
    setCancelling(null);
  };

  const toggleExpand = (id) => setExpanded(e => e === id ? null : id);

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <motion.div className="exp-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 className="exp-section-title" style={{ margin: 0 }}>My Claims</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <motion.button className="exp-btn ghost small" onClick={() => load()} whileTap={{ scale: 0.95 }}>
            <FiRefreshCw size={12} />
          </motion.button>
          <motion.button className="exp-btn primary small" onClick={() => onNavigate('submit')} whileTap={{ scale: 0.96 }}>
            + New Claim
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="exp-filter-bar">
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--e-muted)' }}>Status:</span>
        {ALL_STATUSES.map(s => (
          <motion.button
            key={s || 'all'}
            className={`exp-btn xs ${filterStatus === s ? 'primary' : ''}`}
            onClick={() => handleFilterChange(s)}
            whileTap={{ scale: 0.96 }}
          >
            {s || 'All'}
          </motion.button>
        ))}
      </div>

      {loading ? (
        <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
      ) : claims.length === 0 ? (
        <div className="exp-empty">
          <div className="exp-empty-icon">🧾</div>
          <div className="exp-empty-msg">No claims found</div>
          <div className="exp-empty-sub">{filterStatus ? `No ${filterStatus.replace(/_/g,' ')} claims.` : 'Submit your first expense claim.'}</div>
          <motion.button className="exp-btn primary small" style={{ margin: '12px auto 0' }} onClick={() => onNavigate('submit')} whileTap={{ scale: 0.96 }}>
            + Submit Claim
          </motion.button>
        </div>
      ) : (
        <div className="exp-card">
          <div className="exp-table-wrap" style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
            <table className="exp-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Policy</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {claims.map(c => (
                  <React.Fragment key={c.claim_id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(c.claim_id)}>
                      <td style={{ width: 28, color: 'var(--e-muted)' }}>
                        {expanded === c.claim_id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.description || `Claim ${c.claim_id.slice(0, 8)}…`}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--e-muted)', marginTop: 1 }}>
                          {c.company_code} · {c.currency_code}
                        </div>
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--ep)' }}>
                        {parseFloat(c.total_amount).toLocaleString('en-IN')}
                      </td>
                      <td style={{ color: 'var(--e-muted)', fontSize: '0.84rem' }}>{fmtDate(c.submitted_at)}</td>
                      <td>
                        <StatusBadge status={c.status} />
                        {STATUS_POLLING.has(c.status) && (
                          <div className="exp-spinner" style={{ width: 10, height: 10, borderWidth: 2, marginTop: 3, marginLeft: 2 }} />
                        )}
                      </td>
                      <td><PolicyBadge status={c.policy_status} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        {CAN_CANCEL.has(c.status) && (
                          <motion.button
                            className="exp-icon-btn danger"
                            onClick={() => handleCancel(c.claim_id)}
                            disabled={cancelling === c.claim_id}
                            whileTap={{ scale: 0.92 }}
                            title="Cancel claim"
                          >
                            <FiTrash2 size={13} />
                          </motion.button>
                        )}
                      </td>
                    </tr>
                    <AnimatePresence>
                      {expanded === c.claim_id && (
                        <tr key={`detail-${c.claim_id}`}>
                          <td colSpan={7} style={{ padding: '0 14px 14px' }}>
                            <ClaimDetailPanel claimId={c.claim_id} onClose={() => setExpanded(null)} />
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="exp-pagination">
              <span className="exp-page-info">
                {total} claims · Page {page} of {pages}
              </span>
              <div className="exp-page-btns">
                <motion.button
                  className="exp-btn small"
                  disabled={page === 1}
                  onClick={() => { setPage(p => p - 1); load(page - 1); }}
                  whileTap={{ scale: 0.96 }}
                >
                  ← Prev
                </motion.button>
                <motion.button
                  className="exp-btn small"
                  disabled={page === pages}
                  onClick={() => { setPage(p => p + 1); load(page + 1); }}
                  whileTap={{ scale: 0.96 }}
                >
                  Next →
                </motion.button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
